/**
 * 国优炬康 管理后台 axios 封装
 *
 * 模式参考 FWellness(apps/admin-web/src/utils/request.ts)
 * Mock 通过 axios-mock-adapter + VITE_USE_MOCK=true 开关
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { ElMessage } from 'element-plus';
import MockAdapter from 'axios-mock-adapter';

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';

export interface ApiClient extends Omit<AxiosInstance, 'get' | 'post' | 'put' | 'patch' | 'delete'> {
  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  put<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  patch<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
}

export const instance = axios.create({
  baseURL,
  timeout: 30000,
}) as ApiClient;

const useMock = import.meta.env.VITE_USE_MOCK === 'true';

/** 拦截器已 toast 的标记:页面 catch 里用 showError 判重,避免双弹 */
const TOAST_SHOWN = '__toastShown';

/** 构造统一拒绝对象:message 永远是后端业务文案(不再是 axios 网络层文案) */
const friendlyError = (msg: string, toasted: boolean, cause?: unknown): Error => {
  const err = new Error(msg);
  if (toasted) (err as any)[TOAST_SHOWN] = true;
  if (cause) (err as any).cause = cause;
  return err;
};

/**
 * 页面 catch 统一错误提示入口:
 * - 拦截器已 toast 的请求错误 → 不重复弹;
 * - ElMessageBox 的 'cancel'/'close' 取消信号 → 不弹;
 * - 其余(本地逻辑错误等) → 弹 err.message 或 fallback。
 */
export const showError = (err: unknown, fallback = '操作失败') => {
  if (err === 'cancel' || err === 'close') return;
  if ((err as any)?.[TOAST_SHOWN]) return;
  ElMessage.error((err as Error)?.message || fallback);
};

// 响应拦截器:统一处理 { success, code, message, data, traceId }
instance.interceptors.response.use(
  (response: AxiosResponse) => {
    const data = response.data;
    if (data && typeof data === 'object' && 'success' in data) {
      if (!data.success) {
        // 业务错误(调用方可通过 config.skipErrorToast 自行接管错误展示)
        const msg = data.message || '请求失败';
        const skip = (response.config as any)?.skipErrorToast;
        if (!skip) {
          ElMessage.error(msg);
        }
        return Promise.reject(friendlyError(msg, !skip));
      }
      return data.data;
    }
    return data;
  },
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem('gyjk_admin_token');
      localStorage.removeItem('gyjk_admin_info');
      const base = import.meta.env.BASE_URL || '/';
      const loginPath = `${base.replace(/\/$/, '')}/login`;
      if (!window.location.pathname.endsWith('/login')) {
        window.location.assign(loginPath);
      }
    }
    const msg = error?.response?.data?.message || error?.message || '网络错误';
    const skip = (error?.config as any)?.skipErrorToast;
    if (!skip) {
      ElMessage.error(msg);
    }
    // reject 统一为友好错误(保留原始 axios 错误在 cause 上便于排查),
    // 页面 catch 拿到的 err.message 即业务文案,不再是 "Request failed with status code xxx"
    return Promise.reject(friendlyError(msg, !skip, error));
  },
);

// 请求拦截器:加 Authorization + traceId
instance.interceptors.request.use((config) => {
  const token = localStorage.getItem('gyjk_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['X-Trace-Id'] = `trace-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return config;
});

export { useMock, MockAdapter };
export default instance;
